import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { searchClinicalConcepts, searchIcd10 } from '../../api/catalogues.js'
import { useDebounce } from '../../hooks/useDebounce.js'

/**
 * Clinical Concept Picker (PR #73)
 * ================================
 *
 * The clinician-facing replacement for the raw ICD-10 autocomplete on the
 * CNE diagnosis sections (J Provisional / K Differential / M Final).
 *
 * The doctor types a clinical concept — "diab", "malar", "chest pain",
 * "sik suga" — and sees clinical vocabulary rows ("Type 2 Diabetes",
 * "Malaria — P. falciparum", "Chest Pain — cardiac cause uncertain")
 * instead of scrolling through E11.0..E11.9. The stored ICD-10 code is
 * still valid because every concept maps to a real ICD-10 code
 * (verifiable via the ICD10Code FK on ClinicalConcept).
 *
 * Data flow:
 *   1. Types into input, 200ms debounce.
 *   2. Fetches /api/clinical-notes/concepts/search/ → normalized rows
 *      with `.id` = ICD10Code FK id, `.description` = concept.display_name,
 *      plus concept-layer metadata under `_concept*` keys.
 *   3. Renders rows: bold display_name + subtle code hint + optional
 *      ⭐ Common / Local badges.
 *   4. Clicking a row fires `onPick(normalizedRow)` matching the shape
 *      the existing sync path expects — so `syncIcd10Diagnoses` in
 *      encounters.js needs no changes.
 *
 * Fallback:
 *   The pilot dictionary is ~300 concepts. Rare diagnoses may not be
 *   there. A "Search full ICD-10 catalogue" toggle at the bottom of the
 *   results switches the same input over to the legacy ICD-10 endpoint,
 *   so the doctor never gets stuck.
 *
 * Modifier prompt (Phase B, deferred to a follow-up PR):
 *   Concepts with `_requiresModifier` (e.g. `diabetes_type` on a DKA
 *   concept — Type 1 or Type 2?) will grow a compact prompt after picking.
 *   For now the base concept + code is stored — clinically still correct
 *   at the reporting level, just less granular. The concept metadata
 *   travels through in `_requiresModifier` so the follow-up PR wires the
 *   prompt without further backend work.
 */

export default function ClinicalConceptPicker({
  onPick,
  excludeCodes = [],
  placeholder,
  autoFocus,
}) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const [useFullIcd10, setUseFullIcd10] = useState(false)
  const inputRef = useRef(null)
  const debounced = useDebounce(q, 200)

  // Which search backend to hit — clinician-first by default, ICD-10
  // catalogue on demand via the fallback toggle.
  const queryFn = useFullIcd10
    ? () => searchIcd10(debounced)
    : () => searchClinicalConcepts(debounced)

  const { data = [], isFetching } = useQuery({
    queryKey: [useFullIcd10 ? 'icd10' : 'concept', debounced],
    queryFn,
    enabled: debounced.trim().length >= 2,
    // Popular searches ("diab", "malar", "chest") reuse the URL — 5 min
    // cache saves the round trip.
    staleTime: 5 * 60_000,
  })

  const filtered = data.filter((c) => !excludeCodes.includes(c.code))

  function pick(c) {
    onPick(c)
    setQ('')
    setOpen(false)
    setHighlight(0)
    // Doctors do rapid-fire coding — keep the input focused for the
    // next diagnosis without a re-click.
    inputRef.current?.focus()
  }

  function onKeyDown(e) {
    if (!open) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered[highlight]) pick(filtered[highlight])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const showMenu = open && debounced.trim().length >= 2
  const defaultPlaceholder = useFullIcd10
    ? 'Search full ICD-10 catalogue…'
    : placeholder || 'Type diagnosis or abbreviation (e.g. diab, DM, malar)…'

  return (
    <div className="relative">
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
          setHighlight(0)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder={defaultPlaceholder}
        className="w-full border rounded px-3 py-2 text-sm"
        inputMode="search"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showMenu}
      />
      {showMenu && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto text-sm">
          {isFetching && (
            <div className="p-2 text-slate-400">Searching…</div>
          )}
          {!isFetching && filtered.length === 0 && (
            <div className="p-2 text-slate-400">
              No matching {useFullIcd10 ? 'ICD-10 codes' : 'clinical concepts'}
              {!useFullIcd10 && (
                <>
                  {' — '}
                  <button
                    type="button"
                    className="underline text-wantok-700"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      setUseFullIcd10(true)
                    }}
                  >
                    search full ICD-10 catalogue
                  </button>
                </>
              )}
            </div>
          )}
          {filtered.map((c, i) => (
            <button
              type="button"
              key={c._conceptId || c.code}
              onMouseDown={(e) => {
                e.preventDefault()
                pick(c)
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`block w-full text-left px-3 py-2 ${
                i === highlight ? 'bg-wantok-50' : 'hover:bg-slate-50'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-slate-900">
                  {c.description}
                </span>
                <div className="flex-shrink-0 flex items-center gap-1">
                  {c._localOnly && (
                    <span
                      className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                      title="PNG local presentation — no direct ICD-10 mapping"
                    >
                      Local
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="font-mono text-[11px] text-slate-500">
                  {c.code}
                </span>
                {c.category && (
                  <span className="text-[11px] text-slate-400 capitalize">
                    · {c.category.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            </button>
          ))}
          {/*
            Fallback toggle — surfaces when we're on the concept endpoint
            and results were returned. Doctor can widen to the full ICD-10
            catalogue if the clinical dictionary doesn't have their case.
          */}
          {!useFullIcd10 && !isFetching && filtered.length > 0 && (
            <div className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-400">
              Can't find it?{' '}
              <button
                type="button"
                className="underline text-wantok-700"
                onMouseDown={(e) => {
                  e.preventDefault()
                  setUseFullIcd10(true)
                }}
              >
                Search full ICD-10 catalogue
              </button>
            </div>
          )}
          {useFullIcd10 && !isFetching && (
            <div className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-400">
              Searching full ICD-10.{' '}
              <button
                type="button"
                className="underline text-wantok-700"
                onMouseDown={(e) => {
                  e.preventDefault()
                  setUseFullIcd10(false)
                }}
              >
                Back to clinical concepts
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
